import moment from "moment";
import i18n from "../../../i18n";
import { authoredWeeks } from "../../../utils/flexible-program";
import { buildPreviewWindow } from "../../../utils/flexible-program-preview";
import FlexibleProgramPreview from "./FlexibleProgramPreview";

const PREVIEW_WEEKS = 6;

const FlexibleProgramPreviewController = async ({
  res,
  req,
  locals,
  load,
  api,
  flash,
}) => {
  const { query = {}, params } = req;

  await load("editCycle", {
    cycle: params.cycleId,
    flexibleAthlete: query.athlete,
  });
  locals.query = query;

  const cycle = locals.editCycle;

  // Same guard as flexible-program.controller.js, and the same reason it
  // resolves independently of `cycle` rather than comparing a field on it
  // (e.g. cycle.owner): several actions in actions/cycle.js and
  // actions/exercise.js stash their just-saved cycle into the session under
  // the SAME "editCycle" key with a short TTL, and the editCycle loader
  // returns that session value as-is when present, ignoring which cycle id
  // was actually requested - so `cycle` can't be trusted as "the cycle
  // params.cycleId asked for" close to a save. The dropdown is only ever
  // used to pick a DIFFERENT athlete, so whenever query.athlete is set this
  // is always a genuine switch worth checking.
  let theirCycle = null;
  if (query.athlete) {
    await load("athleteFlexibleProgram", { proxyAthlete: query.athlete });
    theirCycle = locals.athleteFlexibleProgram?.[0] || null;
  }
  const switchedToDifferentCycle =
    query.athlete && theirCycle?._id !== params.cycleId;

  if (!cycle || cycle.storeType !== "flexible" || switchedToDifferentCycle) {
    // organizationCoach has to survive the redirect: when the selected
    // athlete belongs to a different coach in the org, ensure-coach.js only
    // finds them in athletesToRequest when this is present (it's what pulls
    // in coachOrganizationInformation.athletes) - dropping it here would
    // bounce the coach to /coaching-portal/athletes instead.
    const athleteQuery = query.athlete
      ? `?athlete=${query.athlete}${query.organizationCoach ? `&organizationCoach=${query.organizationCoach}` : ""}`
      : "";
    if (theirCycle) {
      return res.redirect(
        `/athlete-portal/programs/flexible-program-preview/${theirCycle._id}${athleteQuery}`
      );
    }
    // Never bounce silently: say why the manager could not be opened.
    flash("error", i18n.t("athlete-portal:flexible.errors.notFound"));
    return res.redirect(`/athlete-portal/programs${athleteQuery}`);
  }

  const weekOffset = Number.parseInt(query.weekOffset, 10) || 0;
  const today = Number(moment().format("YYYYMMDD"));
  const previewWindow = buildPreviewWindow(cycle, {
    todayYyyymmdd: today,
    weekOffset,
    cardCount: PREVIEW_WEEKS,
  });

  locals.weekOffset = weekOffset;
  locals.previewWindow = previewWindow;

  // Only the claim ledger comes from here (prescribed and logged exercises are
  // fetched client-side by the strip). `from` stays the program's first week
  // so the server-side walk is contiguous; `to` reaches the last visible card,
  // which also keeps this a separate cache entry from the manager's own
  // today-capped call.
  const weeks = authoredWeeks(cycle);
  if (weeks.length && previewWindow.cards.length) {
    await load("flexibleProgramOverview", {
      cycleId: params.cycleId,
      from: weeks[0].startDate,
      to: previewWindow.cards[previewWindow.cards.length - 1].cardEnd,
    });
  }

  res.body = <FlexibleProgramPreview locals={locals} api={api} />;
};

export default FlexibleProgramPreviewController;
